import axios from 'axios';
import _ from 'lodash';
import { Chatroom } from '../../types';
import { getTokenFromLocalStorage } from './local-storage';

const getBaseURL = () => {
	return window.location.host.includes('local')
		? 'https://local-chat-web-server.relay.delivery'
		: window.location.host.includes('dev')
		? 'https://dev-chat-web-server.relay.delivery'
		: 'https://chat-web-server.relay.delivery';
};

const delay = (ms) => {
	return new Promise((resolve) => {
		setTimeout(resolve, ms);
	});
};

export const fetchLatestChatroomForOrder = async (orderKey: string) => {
	const token = getTokenFromLocalStorage();

	const CHAT_WEBSERVER_BASE_URL = getBaseURL();

	const response = await axios.get(
		`${CHAT_WEBSERVER_BASE_URL}/search-chatrooms`,
		{
			params: {
				orderKey,
				chatroomType: 'superadmin_chat',
				showClosedChatrooms: true,
			},
			headers: {
				'x-relay-auth': token,
			},
		}
	);

	const chatrooms =
		response.data?.chatrooms?.sort(
			(a, b) =>
				new Date(b.updatedAt).getTime() -
				new Date(a.updatedAt).getTime()
		) ?? [];

	return chatrooms[0] ?? undefined;
};

export const openChatroom = async (orderKey: string, chatTitle) => {
	const token = getTokenFromLocalStorage();

	const CHAT_WEBSERVER_BASE_URL = getBaseURL();

	const response = await axios.post(
		CHAT_WEBSERVER_BASE_URL + '/chatroom',
		{ orderKey, chatTitle },
		{
			headers: { 'x-relay-auth': token },
		}
	);

	return response.data;
};

// const icon = "/public/images/logoimage.png"; TODO can't get this to work 
const handleChatrooms = ({chatrooms, setChatrooms, setNotifications, chatNotificationsEnabled, firstFetch = true}) => {
	const chatroomLookup: Record<string, Chatroom> = _.chain(chatrooms)
		.groupBy((c) => c.orderKey || c.chatroomKey)
		.mapValues((chatroomArray) => {
			const chatroom = _.head(chatroomArray);
			const { hasBeenReadByParticipant, chatroomKey, orderKey, chatTitle, lastRecord = {} } = chatroom;
			const {
				translations = {},
				timestamp: lastRecordTimestamp,
				participantType: lastRecordParticipantType,
			} = lastRecord;
			const lastMessage = translations.en;
			return {
				hasBeenReadByParticipant,
				chatroomKey,
				orderKey,
				chatTitle,
				...(lastRecordParticipantType === 'superadmin'
					? {}
					: { lastRecordReceivedTimestamp: new Date(lastRecordTimestamp), lastReceivedMessage: lastMessage }),
			};
		})
		.value();

	setNotifications((prevNotificationLog) => {
		const newNotificationLog = _.cloneDeep(prevNotificationLog || {});
		Object.values(chatroomLookup).forEach((chatroom) => {
			const {chatroomKey, lastRecordReceivedTimestamp, chatTitle, lastReceivedMessage} = chatroom;
			if (!lastRecordReceivedTimestamp) return;
			
			if (
				!prevNotificationLog ||
				!(prevNotificationLog[chatroomKey]) ||
				new Date (prevNotificationLog[chatroomKey]) < lastRecordReceivedTimestamp
			) {
				if (!firstFetch && chatNotificationsEnabled) { // don't blast all new notifications on first chatroom fetch
					const notification = new Notification(chatTitle, { body: lastReceivedMessage });
					notification.onclick = () => {
						window.open(`/chatrooms/${chatroomKey}`);
					};
				}
				newNotificationLog[chatroomKey] = lastRecordReceivedTimestamp;
			}
		});

		if (_.isEqual(prevNotificationLog, newNotificationLog)) {
			return prevNotificationLog;
		}
		return newNotificationLog;
	});

	setChatrooms((prevLookup) => {
		const mergedLookup = _.assign({}, prevLookup, chatroomLookup);
		if (_.isEqual(prevLookup, mergedLookup)) {
			return prevLookup;
		}
		return mergedLookup;
	});
};

export const pollChatrooms = async ({
	timestamp = null,
	signal,
	ignoreCache = false,
	setChatrooms,
	setNotifications,
	chatNotificationsEnabled = false,
}: {
	timestamp?: number | null;
	signal: any;
	ignoreCache?: boolean;
	setChatrooms: (chatrooms: Record<string, any>) => void;
	setNotifications: (notifications: Record<string, any>) => void;
	chatNotificationsEnabled: boolean;
}) => {
	const token = getTokenFromLocalStorage();

	const CHAT_WEBSERVER_BASE_URL = getBaseURL();

	try {
		const url = !timestamp
			? new URL(`${CHAT_WEBSERVER_BASE_URL}/chatrooms?limit=50&showClosedChatrooms=true&ignoreCache=${ignoreCache}`)
			: new URL(`${CHAT_WEBSERVER_BASE_URL}/chatrooms/${timestamp}`);

		const res = await fetch(url.toString(), {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				'x-relay-auth': token ?? '',
			},
			...(signal && { signal }),
		});

		if (res.status === 404) { 
			global.logger.info('getting updates failed, polling again');
			await delay(1000);
			return pollChatrooms({ signal, ignoreCache: true, setChatrooms, setNotifications, chatNotificationsEnabled }); 
		}

		if (res.status !== 200) {
			throw new Error(`pollChatrooms: ${res.status}`);
		}

		const {chatrooms, timestamp: newTimestamp} = await res.json();
		handleChatrooms({chatrooms, setChatrooms, setNotifications, firstFetch: !timestamp, chatNotificationsEnabled});
		return pollChatrooms({ timestamp: newTimestamp, signal, setChatrooms, setNotifications, chatNotificationsEnabled });
		
	} catch (err: any) {
		const isAborted = err instanceof DOMException;

		const message = `pollChatrooms: ${err.message}`;

		if (isAborted) {
			console.log(message);
			return null;
		} else {
			console.error(message);
			const randomSeconds = Math.floor(Math.random() * 61 + 30) * 1000;
			await delay(randomSeconds);

			return pollChatrooms({
				timestamp,
				signal,
				ignoreCache: true,
				setChatrooms,
				setNotifications,
				chatNotificationsEnabled,
			});
		}
	}
};

export const sendMessage = async (chatroomKey, message) => {
	const token = getTokenFromLocalStorage();

	const CHAT_WEBSERVER_BASE_URL = getBaseURL();

	if (!token) return;

	try {
		const url = new URL(
			`${CHAT_WEBSERVER_BASE_URL}/chatroom/${chatroomKey}/message`
		);
		await fetch(url.toString(), {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'x-relay-auth': token,
			},
			body: JSON.stringify({
				message,
			}),
		});
		return true;
	} catch (err) {
		console.error('sendMessage error');
		return false;
	}
};
